import { ElementType } from "react";
import { keyframes, Theme } from "@emotion/react";
import styled from "@emotion/styled";

import { pulseOpacity } from "../../../styles/animations";

type SkeletonVariant = "rounded" | "rectangular";

type SkeletonTextProps = {
  variant?: SkeletonVariant;
  width: string;
  fontSize?: number;
  lineHeight?: number;
  height?: number;
  backgroundColor?: keyof Theme["colors"];
  animationDelayMs?: number;
  customSpacing?: boolean;
  shimmer?: boolean;
  as?: ElementType;
};

// One band of light per tile. The gradient is fixed to the viewport, so every bar on screen
// shares the same wave instead of each sweeping on its own.
const SHIMMER_TILE_PX = 1200;
const shimmerSweep = keyframes({
  from: { backgroundPosition: "0 0" },
  to: { backgroundPosition: `${SHIMMER_TILE_PX}px 0` },
});

/**
 * @param variant - The shape of the skeleton
 * @param width - The width of the skeleton
 * @param fontSize - Set to the font size of the text you are replacing
 * @param lineHeight - Set to the line height of the text you are replacing (d)
 * @param height - Overrides the height derived from fontSize, for block-shaped placeholders
 * @param backgroundColor - Colour from {@link Theme.colors}
 * @param customSpacing - If true, disables the internal margin to allow custom spacing via parent layout
 * @param shimmer - Sweeps a band of light across the bar instead of pulsing its opacity

 */
const SkeletonText = styled.div<SkeletonTextProps>(
  ({
    variant = "rounded",
    theme,
    width,
    fontSize = 14,
    lineHeight = 1.57,
    height,
    backgroundColor = "grey50",
    animationDelayMs = 0,
    as,
    customSpacing = false,
    shimmer = false,
  }) => ({
    ...(shimmer
      ? {
          backgroundColor: theme.colors[backgroundColor],
          backgroundImage: `linear-gradient(90deg, transparent 0, transparent 35%, ${theme.colors.grey10} 50%, transparent 65%, transparent 100%)`,
          backgroundSize: `${SHIMMER_TILE_PX}px 100%`,
          backgroundAttachment: "fixed",
          animation: `${shimmerSweep} 1.6s linear infinite`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }
      : {
          animation: `${pulseOpacity} 2s infinite`,
          opacity: 0.5, // To match the initial state of the pulseOpacity animation
          animationDelay: `${animationDelayMs}ms`,
          backgroundColor: theme.colors[backgroundColor],
        }),
    borderRadius: variant === "rounded" ? "9999px" : "6px", // Not using 100% as we just want rounded corners, not an oval
    height: height ?? fontSize,
    margin: customSpacing
      ? 0
      : `${(fontSize * lineHeight - fontSize) / 2}px 0${as === "span" ? " 0 0" : ""}`, // Disable bottom margin if it's a span
    width,
    ...(as === "span" && {
      display: "inline-block",
      verticalAlign: "text-bottom",
    }),
  }),
);

export default SkeletonText;
